'use strict';

const BLOCKED_KEYS = new Set([
    '__proto__',
    'constructor',
    'prototype',
    'api_response_id',
    'previous_response_id',
    'provider_response_id',
    'response_id',
    'store',
    'provider_state',
    'reasoning',
    'chain_of_thought',
]);

const REVISION_STATUSES = {
    pending: {label: '결정 대기', variant: 'warning'},
    applied: {label: '적용 완료', variant: 'success'},
    rejected: {label: '거절됨', variant: 'secondary'},
    superseded: {label: '새 수정안으로 대체됨', variant: 'secondary'},
    conflicted: {label: '현재 평가와 충돌', variant: 'danger'},
};

function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function safeScalar(value) {
    return value === null
        || typeof value === 'string'
        || typeof value === 'boolean'
        || (typeof value === 'number' && Number.isFinite(value));
}

function sanitizeJson(value, depth = 0) {
    if (depth > 20) return null;
    if (safeScalar(value)) return value;
    if (Array.isArray(value)) {
        return value
            .map((item) => sanitizeJson(item, depth + 1))
            .filter((item) => item !== undefined);
    }
    if (!isObject(value)) return undefined;

    return Object.keys(value).sort().reduce((clean, key) => {
        if (BLOCKED_KEYS.has(String(key).toLowerCase())) return clean;
        const sanitized = sanitizeJson(value[key], depth + 1);
        if (sanitized !== undefined) clean[key] = sanitized;
        return clean;
    }, {});
}

function copyId(value) {
    return typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value))
        ? value
        : undefined;
}

function copyText(value) {
    return typeof value === 'string' ? value : undefined;
}

function copyTimestamp(value) {
    return typeof value === 'string' || value === null ? value : undefined;
}

function assignDefined(target, key, value) {
    if (value !== undefined) target[key] = value;
    return target;
}

function sanitizeQnaItem(item) {
    if (!isObject(item) || !['user', 'assistant'].includes(item.role) || typeof item.content !== 'string') {
        return null;
    }
    const clean = {};
    assignDefined(clean, 'id', copyId(item.id));
    clean.role = item.role;
    clean.content = item.content;
    assignDefined(clean, 'created_at', copyTimestamp(item.created_at));
    if (item.degraded === true || item.degraded_context === true) clean.degraded = true;
    if (item.optimistic === true) clean.optimistic = true;
    return clean;
}

function sanitizeQnaItems(items) {
    if (!Array.isArray(items)) return [];
    return items.map(sanitizeQnaItem).filter(Boolean);
}

function normalizeReason(reason) {
    if (!isObject(reason)) return null;
    const clean = {};
    assignDefined(clean, 'comment', copyText(reason.comment));
    if (isObject(reason.category)) {
        const category = {};
        assignDefined(category, 'code', copyText(reason.category.code));
        assignDefined(category, 'category', copyText(reason.category.category));
        if (Object.keys(category).length) clean.category = category;
    }
    if (Array.isArray(reason.evidence)) clean.evidence = sanitizeJson(reason.evidence);
    return Object.keys(clean).length ? clean : null;
}

function normalizeEvaluationProcess(process) {
    if (!isObject(process)) return null;
    const clean = {};
    if (Number.isFinite(process.schema_version)) clean.schemaVersion = process.schema_version;
    assignDefined(clean, 'source', copyText(process.source));
    assignDefined(clean, 'criterionInterpretation', copyText(process.criterion_interpretation));
    if (Array.isArray(process.evidence_checks)) {
        clean.evidenceChecks = process.evidence_checks.map((check) => {
            if (!isObject(check)) return null;
            const normalized = {};
            ['filename', 'location', 'finding', 'relevance'].forEach((key) => {
                assignDefined(normalized, key, copyText(check[key]));
            });
            return Object.keys(normalized).length ? normalized : null;
        }).filter(Boolean);
    }
    assignDefined(clean, 'ruleApplication', copyText(process.rule_application));
    if (Array.isArray(process.limitations)) {
        clean.limitations = process.limitations.filter((item) => typeof item === 'string');
    }
    assignDefined(clean, 'conclusion', copyText(process.conclusion));
    return Object.keys(clean).length ? clean : null;
}

function normalizeCurrentEvaluation(evaluation) {
    if (!isObject(evaluation)) return null;
    const clean = {};
    assignDefined(clean, 'evId', copyId(evaluation.ev_id));
    if (typeof evaluation.confidence === 'number' && Number.isFinite(evaluation.confidence)) {
        clean.confidence = evaluation.confidence;
    }
    if (typeof evaluation.is_satisfied === 'boolean') clean.isSatisfied = evaluation.is_satisfied;
    const reason = normalizeReason(evaluation.reason);
    if (reason) clean.reason = reason;
    const process = normalizeEvaluationProcess(evaluation.evaluation_process);
    if (process) clean.evaluationProcess = process;
    return Object.keys(clean).length ? clean : null;
}

function normalizeInitialMessage(message) {
    if (!isObject(message)) return null;
    const currentEvaluation = normalizeCurrentEvaluation(message.current_evaluation);
    if (!currentEvaluation) return null;
    const clean = {currentEvaluation};
    assignDefined(clean, 'kind', copyText(message.kind));
    assignDefined(clean, 'source', copyText(message.source));
    clean.isReconstructed = message.is_reconstructed === true || message.source === 'reconstructed';
    return clean;
}

function normalizeAffectedItem(item) {
    if (!isObject(item)) return null;
    const clean = {};
    assignDefined(clean, 'evId', copyId(item.ev_id ?? item.after?.ev_id ?? item.before?.ev_id));
    if ('before' in item) clean.before = sanitizeJson(item.before);
    if ('after' in item) clean.after = sanitizeJson(item.after);
    return Object.keys(clean).length ? clean : null;
}

function normalizeRevision(revision) {
    if (!isObject(revision)) return null;
    const clean = {};
    assignDefined(clean, 'id', copyId(revision.id));
    assignDefined(clean, 'auditId', copyId(revision.audit_id));
    assignDefined(clean, 'evId', copyId(revision.ev_id));
    assignDefined(clean, 'qnaAnswerId', copyId(revision.qna_answer_id));
    assignDefined(clean, 'status', copyText(revision.status));
    assignDefined(clean, 'explanation', copyText(revision.explanation));
    assignDefined(clean, 'createdAt', copyTimestamp(revision.created_at));
    assignDefined(clean, 'decidedAt', copyTimestamp(revision.decided_at));
    if (isObject(revision.before_item_json)) clean.beforeItem = sanitizeJson(revision.before_item_json);
    if (isObject(revision.patch_json)) clean.patch = sanitizeJson(revision.patch_json);

    if (isObject(revision.preview)) {
        const preview = {stale: revision.preview.stale === true};
        preview.affectedItems = Array.isArray(revision.preview.affected_items)
            ? revision.preview.affected_items.map(normalizeAffectedItem).filter(Boolean)
            : [];
        if (typeof revision.preview.confidence === 'number' && Number.isFinite(revision.preview.confidence)) {
            preview.confidence = revision.preview.confidence;
        }
        assignDefined(preview, 'action', copyText(revision.preview.action));
        clean.preview = preview;
    }
    return copyId(revision.id) !== undefined || copyText(revision.status) !== undefined ? clean : null;
}

function normalizeQnaPayload(payload) {
    const data = isObject(payload) ? payload : {};
    return {
        items: sanitizeQnaItems(data.items),
        initialMessage: normalizeInitialMessage(data.initial_message),
        pendingRevision: normalizeRevision(data.pending_revision),
        revisionHistory: Array.isArray(data.revision_history)
            ? data.revision_history.map(normalizeRevision).filter(Boolean)
            : [],
    };
}

function normalizePostedMessage(message, degraded) {
    if (!isObject(message) || typeof message.content !== 'string') return null;
    const clean = {};
    assignDefined(clean, 'id', copyId(message.id));
    clean.content = message.content;
    assignDefined(clean, 'created_at', copyTimestamp(message.created_at));
    if (degraded !== undefined) clean.degraded = degraded === true;
    return clean;
}

function normalizeQnaPostResponse(payload) {
    const data = isObject(payload) ? payload : {};
    return {
        question: normalizePostedMessage(data.question),
        answer: normalizePostedMessage(data.answer, data.degraded_context === true),
        revisionProposal: normalizeRevision(data.revision_proposal),
    };
}

function requiredUrlPart(value, name) {
    if (value === null || value === undefined || value === '') {
        throw new TypeError(`${name} is required`);
    }
    return encodeURIComponent(String(value));
}

function createBodylessRevisionRequest(auditId, revisionId, action) {
    if (!['apply', 'reject'].includes(action)) throw new TypeError('action must be apply or reject');
    const auditPart = requiredUrlPart(auditId, 'auditId');
    const revisionPart = requiredUrlPart(revisionId, 'revisionId');
    return {
        method: 'post',
        url: `/assessments/ai-audit/${auditPart}/qna/revisions/${revisionPart}/${action}`,
    };
}

function isRevisionInteractionBusy(busy = false) {
    if (typeof busy === 'boolean') return busy;
    return isObject(busy) && (busy.decisionBusy === true || busy.sending === true);
}

function canApplyRevision(revision, busy = false) {
    return !isRevisionInteractionBusy(busy)
        && revision?.status === 'pending'
        && revision?.preview?.stale !== true;
}

function canRejectRevision(revision, busy = false) {
    return !isRevisionInteractionBusy(busy) && revision?.status === 'pending';
}

function childPath(path, key, parentIsArray) {
    if (parentIsArray) return path ? `${path}[${key}]` : `[${key}]`;
    return path ? `${path}.${key}` : key;
}

function valuesEqual(before, after) {
    if (before === after) return true;
    if (Number.isNaN(before) && Number.isNaN(after)) return true;
    return false;
}

function flattenLeafDiffs(before, after, path = '') {
    if (valuesEqual(before, after)) return [];
    const beforeContainer = isObject(before) || Array.isArray(before);
    const afterContainer = isObject(after) || Array.isArray(after);
    const sameContainerKind = (Array.isArray(before) && Array.isArray(after))
        || (isObject(before) && isObject(after));

    if (beforeContainer && afterContainer && sameContainerKind) {
        const isArray = Array.isArray(before);
        const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort((left, right) => {
            if (isArray) return Number(left) - Number(right);
            return left.localeCompare(right);
        });
        return keys.flatMap((key) => flattenLeafDiffs(
            before[key],
            after[key],
            childPath(path, key, isArray),
        ));
    }
    return [{path: path || '$', before, after}];
}

function revisionStatusMeta(status) {
    return REVISION_STATUSES[status] || {label: '알 수 없음', variant: 'dark'};
}

// 백엔드 routing_router 의 action 값. 모르는 값은 원래 값 그대로 보인다.
const REVISION_ACTIONS = {
    auto_approve: '자동 승인',
    human_review: '사람 검토',
    manual_review: '수동 검토',
    reject: '신뢰도 낮음 (사람 검토)',
};

function revisionActionLabel(action) {
    return REVISION_ACTIONS[action] || action || '';
}

function formatDiffValue(value) {
    if (value === undefined) return '없음';
    if (value === null) return '값 없음';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
}

const satisfiedText = (value) => (value === true ? '충족' : value === false ? '미충족' : '판정 없음');
const VERDICT_TEXTS = {true: '충족', false: '미충족', unknown: '판단 불가'};
const EVIDENCE_CHECK_LABELS = {filename: '파일', location: '위치', finding: '확인 내용', relevance: '평가 기준과의 관계'};

// [경로 패턴, 라벨(캡처한 번호는 1부터), 값 표시] — 값 표시가 없으면 formatDiffValue
const DIFF_FIELDS = [
    [/^is_satisfied$/, () => '충족 여부', satisfiedText],
    [/^not_applicable$/, () => '해당없음', (value) => (value === true ? '해당없음' : '해당없음 아님')],
    [/^confidence$/, () => '신뢰도', (value) => (typeof value === 'number' ? `${Math.round(value * 100)}%` : formatDiffValue(value))],
    [/^(?:reason\.verdict|ai_verdict)$/, () => 'AI 판정', (value) => VERDICT_TEXTS[value] || formatDiffValue(value)],
    [/^reason\.comment$/, () => 'AI 판단 설명'],
    [/^reason\.category\.category$/, () => '판단 분류'],
    [/^reason\.category\.code$/, () => '판단 분류 코드'],
    [/^reason\.evidence\[(\d+)\]$/, (n) => `근거 ${n}`,
        (value) => (isObject(value) ? [value.filename, value.basis].filter(Boolean).join('\n') : formatDiffValue(value))],
    [/^reason\.evidence\[(\d+)\]\.basis$/, (n) => `근거 ${n} — 판단 근거`],
    [/^reason\.evidence\[(\d+)\]\.filename$/, (n) => `근거 ${n} — 파일`],
    [/^evaluation_process\.criterion_interpretation$/, () => '평가 기준 해석'],
    [/^evaluation_process\.rule_application$/, () => '판정 규칙 적용'],
    [/^evaluation_process\.conclusion$/, () => '결론'],
    [/^evaluation_process\.limitations\[(\d+)\]$/, (n) => `확인 한계 ${n}`],
    [/^evaluation_process\.evidence_checks\[(\d+)\]\.(filename|location|finding|relevance)$/,
        (n, key) => `확인 근거 ${n} — ${EVIDENCE_CHECK_LABELS[key]}`],
];

// 수정안의 변경 한 줄(flattenLeafDiffs 원소)을 평가자가 읽는 말로. known=false 면 라벨이 원래 경로다.
function describeDiff(path, before, after) {
    for (const [pattern, label, format = formatDiffValue] of DIFF_FIELDS) {
        const match = pattern.exec(path);
        if (!match) continue;
        const parts = match.slice(1).map((part) => (/^\d+$/.test(part) ? Number(part) + 1 : part));
        return {label: label(...parts), beforeText: format(before), afterText: format(after), known: true};
    }
    return {label: path, beforeText: formatDiffValue(before), afterText: formatDiffValue(after), known: false};
}

function confirmedUserTurnCount(items) {
    if (!Array.isArray(items)) return 0;
    return items.filter((item) => item?.role === 'user' && item.optimistic !== true).length;
}

function isRecoveredQuestion(items, questionText) {
    if (!Array.isArray(items) || typeof questionText !== 'string') return false;
    const lastConfirmedUser = [...items].reverse().find(
        (item) => item?.role === 'user' && item.optimistic !== true,
    );
    return lastConfirmedUser?.content === questionText;
}

module.exports = {
    canApplyRevision,
    canRejectRevision,
    confirmedUserTurnCount,
    createBodylessRevisionRequest,
    describeDiff,
    flattenLeafDiffs,
    formatDiffValue,
    isRecoveredQuestion,
    isRevisionInteractionBusy,
    normalizeCurrentEvaluation,
    normalizeInitialMessage,
    normalizeQnaPayload,
    normalizeQnaPostResponse,
    normalizeRevision,
    revisionActionLabel,
    revisionStatusMeta,
    sanitizeQnaItems,
};
